import React from 'react';
import { Helmet } from 'react-helmet';
import BreadcrumbSchema from '../components/BreadcrumbSchema';

// WhatsApp config (edit the phone number or messages here if needed)
const WHATSAPP_PHONE = '522292072733';

const buildWhatsAppHref = (message) =>
  `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(message)}`;

const services = [
  {
    id: 'torres-led',
    title: 'Renta de torres de iluminación LED',
    alt: 'Renta de torres de iluminación LED',
    description:
      'Renta de torres de iluminación LED de alta eficiencia para obras nocturnas, eventos, plataformas industriales y proyectos de construcción. Mayor cobertura lumínica con menor consumo de combustible.',
    image:
      'https://customer-assets.emergentagent.com/job_85f17f41-4b0f-4f9f-928c-2851bf4c5084/artifacts/hdtdtcgn_Copy%20of%20Copy%20of%20Gold%20and%20White%20Modern%20Wedding%20Instagram%20Post%20%285.5%20x%208.5%20cm%29.png',
    message:
      'Hola GENSOL, me interesa la renta de torres de iluminación LED. ¿Me pueden enviar una cotización?',
  },
  {
    id: 'torres-mantenimiento',
    title: 'Reparación y mantenimiento de torres luminarias',
    alt: 'Reparación y mantenimiento de torres luminarias',
    description:
      'Servicio de reparación y mantenimiento de torres luminarias: diagnóstico eléctrico, cambio de focos LED, motor, mástil hidráulico y mantenimiento preventivo o correctivo en sitio.',
    image:
      'https://images.pexels.com/photos/257700/pexels-photo-257700.jpeg?auto=compress&cs=tinysrgb&w=900',
    message:
      'Hola GENSOL, necesito servicio de reparación y mantenimiento de torres luminarias. ¿Me pueden ayudar?',
  },
];

const TorresIluminacionPage = () => {
  return (
    <>
      <Helmet>
        <title>Renta de Torres de Iluminación LED y Mantenimiento | GENSOL Veracruz</title>
        <meta
          name="description"
          content="Renta de torres de iluminación LED y servicio de reparación y mantenimiento de torres luminarias en Veracruz y sureste de México. Entrega rápida, soporte 24/7."
        />
        <meta
          name="keywords"
          content="renta torres de iluminación LED, torres luminarias, reparación torres luminarias, mantenimiento torres de iluminación, Veracruz, sureste México"
        />
        <link
          rel="canonical"
          href="https://www.gensolver.com/servicios/torres-de-iluminacion"
        />
        <meta
          property="og:title"
          content="Renta de Torres de Iluminación LED y Mantenimiento | GENSOL"
        />
        <meta
          property="og:description"
          content="Renta de torres de iluminación LED y servicio de reparación y mantenimiento de torres luminarias. Cotiza por WhatsApp."
        />
        <meta
          property="og:url"
          content="https://www.gensolver.com/servicios/torres-de-iluminacion"
        />
      </Helmet>

      <BreadcrumbSchema
        items={[
          { name: 'Inicio', url: 'https://www.gensolver.com/' },
          { name: 'Servicios', url: 'https://www.gensolver.com/servicios' },
          {
            name: 'Torres de Iluminación',
            url: 'https://www.gensolver.com/servicios/torres-de-iluminacion',
          },
        ]}
      />

      <main
        id="torres-de-iluminacion"
        data-page="torres-de-iluminacion"
        data-analytics-page="torres-de-iluminacion"
        className="pt-24"
      >
        {/* Page Header */}
        <section className="py-12 bg-gray-200">
          <div className="container mx-auto px-4 text-center">
            <h1
              data-testid="torres-page-title"
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
            >
              Renta de <span className="text-orange-500">Torres de Iluminación</span>
            </h1>
            <p className="text-gray-700 max-w-2xl mx-auto">
              Renta de torres de iluminación LED y servicio de reparación y mantenimiento de torres
              luminarias para obras, eventos e industria en Veracruz y el sureste de México.
            </p>
          </div>
        </section>

        {/* Cards */}
        <section className="py-16 bg-gray-100">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {services.map((service) => (
                <article
                  key={service.id}
                  data-testid={`torres-card-${service.id}`}
                  className="group flex flex-col bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-xl hover:border-orange-500/50 transition-all duration-300 overflow-hidden"
                >
                  {/* Image container */}
                  <div className="relative w-full aspect-[4/3] overflow-hidden bg-gray-200">
                    <img
                      src={service.image}
                      alt={service.alt}
                      title={service.alt}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>

                  {/* Body */}
                  <div className="flex flex-col flex-1 p-6">
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 group-hover:text-orange-500 transition-colors">
                      {service.title}
                    </h2>
                    <p className="text-gray-600 mb-6 flex-1">{service.description}</p>

                    <a
                      href={buildWhatsAppHref(service.message)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`torres-cta-${service.id}`}
                      data-analytics-click={`torres-whatsapp-${service.id}`}
                      className="inline-flex items-center justify-center gap-2 w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-300 hover:scale-[1.02]"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                      </svg>
                      Cotizar por WhatsApp
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Trust strip */}
        <section className="py-12 bg-gray-200">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              ¿Necesitas asesoría para tu{' '}
              <span className="text-orange-500">torre de iluminación</span>?
            </h2>
            <p className="text-gray-700 max-w-2xl mx-auto mb-6">
              Te asesoramos para elegir la torre LED ideal según el área a iluminar, autonomía
              requerida y condiciones de tu obra o evento.
            </p>
            <a
              href={buildWhatsAppHref(
                'Hola GENSOL, me gustaría asesoría para rentar o reparar una torre de iluminación.'
              )}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="torres-cta-advisory"
              data-analytics-click="torres-whatsapp-advisory"
              className="inline-flex items-center gap-2 bg-black hover:bg-gray-800 text-white font-semibold px-6 py-3 rounded-xl transition-all duration-300 hover:scale-105"
            >
              Hablar con un asesor
            </a>
          </div>
        </section>
      </main>
    </>
  );
};

export default TorresIluminacionPage;
